import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingCart, User, LogOut, Phone, BookOpen, Search, Menu, X, ShieldCheck, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import CurrencyToggle from "@/components/CurrencyToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, loading, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);

  const onSearch = (e) => {
    e.preventDefault();
    const query = q.trim();
    navigate(query ? `/books?q=${encodeURIComponent(query)}` : "/books");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border" data-testid="site-header">
      {/* Contact strip */}
      <div className="bg-[#09090b] text-white text-xs md:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
          <span className="hidden md:inline font-medium tracking-wide">FREE DELIVERY ACROSS INDIA ON BULK ORDERS.&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;WORLDWIDE SHIPPING AVAILABLE.</span>
          <a
            href="tel:9953272630"
            data-testid="header-phone-link"
            className="inline-flex items-center gap-2 text-[#fb923c] font-semibold hover:text-white transition-colors"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className="tracking-wider">+91 99532 72630</span>
          </a>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 py-4">
          <Link to="/" className="flex items-center gap-3 shrink-0 group" data-testid="brand-link">
            <div className="relative w-12 h-12 md:w-14 md:h-14 shrink-0">
              {/* Glow ring */}
              <div className="absolute inset-0 rounded-full bg-[#facc15] opacity-40 blur-md group-hover:opacity-70 transition-opacity" aria-hidden />
              {/* Logo */}
              <img
                src="https://customer-assets.emergentagent.com/job_priya-book-platform/artifacts/j5n2tcp1_IMG_20211220_171122.jpg"
                alt="GAINA THE PRELUDE"
                className="relative w-full h-full rounded-full object-cover ring-2 ring-[#facc15] shadow-[0_4px_14px_-2px_rgba(234,88,12,0.35)] group-hover:ring-[#ea580c] group-hover:shadow-[0_6px_20px_-2px_rgba(234,88,12,0.55)] transition-all duration-300"
                data-testid="brand-logo"
              />
            </div>
            <div className="leading-tight">
              <div className="font-display text-xl md:text-3xl font-black tracking-tight text-[#ea580c]" data-testid="brand-gaina">GAINA</div>
              <div className="font-display text-[10px] md:text-xs font-bold tracking-[0.25em] text-[#ea580c]">THE PRELUDE</div>
            </div>
          </Link>

          {/* Search */}
          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl">
            <div className="flex w-full border-2 border-[#09090b]">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search books by title, author, or ISBN…"
                data-testid="header-search-input"
                className="flex-1 px-4 py-2.5 text-sm outline-none font-medium"
              />
              <button type="submit" data-testid="header-search-submit" className="bg-[#09090b] text-white px-5 hover:bg-[#ea580c] transition-colors">
                <Search className="w-4 h-4" />
              </button>
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <CurrencyToggle className="hidden sm:inline-flex" />
            <Link to="/cart" className="relative p-2 hover:bg-[#f4f4f5] transition-colors" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5" strokeWidth={2} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[#ea580c] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </Link>

            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="header-admin-link"
                    className="hidden sm:inline-flex items-center gap-1.5 bg-[#09090b] hover:bg-[#ea580c] text-white px-3 py-2 font-bold text-xs uppercase tracking-wider transition-colors"
                    title="Admin Panel"
                  >
                    <ShieldCheck className="w-4 h-4 text-[#facc15]" />
                    Admin
                  </Link>
                )}
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="p-2 hover:bg-[#f4f4f5] transition-colors flex items-center gap-1">
                    <User className="w-5 h-5" strokeWidth={2} />
                    <span className="hidden md:inline text-sm font-semibold">{user.name?.split(" ")[0] || "Account"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-none w-56">
                  <DropdownMenuLabel className="font-display">
                    <div className="text-xs text-[#52525b] uppercase tracking-wider">Signed in as</div>
                    <div className="truncate text-sm">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                    <ShoppingCart className="w-4 h-4 mr-2" /> My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")} data-testid="menu-wishlist">
                    <Heart className="w-4 h-4 mr-2" /> My Wishlist
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <ShieldCheck className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : loading ? (
              <div className="w-16 h-8" />
            ) : (
              <>
                <Link to="/orders" className="hidden md:inline text-xs font-bold uppercase tracking-wider text-[#09090b] hover:text-[#ea580c] transition-colors" data-testid="track-order-link">
                  Track Order
                </Link>
                <Link to="/register" data-testid="register-link">
                  <Button size="sm" className="rounded-none bg-[#ea580c] hover:bg-[#c2410c] text-white font-semibold">Sign up</Button>
                </Link>
              </>
            )}

            <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Category strip */}
        <nav className="hidden md:flex items-center gap-6 pb-3 text-sm font-semibold text-[#09090b]">
          <Link to="/books" className="hover:text-[#ea580c] transition-colors" data-testid="nav-all-books">All Books</Link>
          <Link to="/categories" className="hover:text-[#ea580c] transition-colors" data-testid="nav-categories">Categories</Link>
          <Link to="/books?new_arrival=true" className="hover:text-[#ea580c] transition-colors font-bold text-[#ea580c]" data-testid="nav-new-arrivals">✨ New Arrivals</Link>
          <Link to="/books?category=History" className="hover:text-[#ea580c] transition-colors">History</Link>
          <Link to="/books?category=Art and Archaeology" className="hover:text-[#ea580c] transition-colors">Art</Link>
          <Link to="/books?category=Civil Engineering" className="hover:text-[#ea580c] transition-colors">Engineering</Link>
          <Link to="/books?category=Children Books" className="hover:text-[#ea580c] transition-colors">Children Books</Link>
          <Link to="/books?category=Old and Rare Books" className="hover:text-[#ea580c] transition-colors">Old &amp; Rare</Link>
        </nav>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white p-4 space-y-3">
          <form onSubmit={onSearch} className="flex border-2 border-[#09090b]">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search books…"
              data-testid="mobile-search-input"
              className="flex-1 px-3 py-2 text-sm outline-none"
            />
            <button type="submit" className="bg-[#09090b] text-white px-4">
              <Search className="w-4 h-4" />
            </button>
          </form>
          <div className="flex flex-col gap-2 text-sm font-semibold">
            <Link to="/books" onClick={() => setMobileOpen(false)}>All Books</Link>
            <Link to="/categories" onClick={() => setMobileOpen(false)}>Categories</Link>
            <Link to="/books?new_arrival=true" onClick={() => setMobileOpen(false)}>✨ New Arrivals</Link>
            <Link to="/books?category=History" onClick={() => setMobileOpen(false)}>History</Link>
            <Link to="/books?category=Art and Archaeology" onClick={() => setMobileOpen(false)}>Art &amp; Archaeology</Link>
            <Link to="/books?category=Civil Engineering" onClick={() => setMobileOpen(false)}>Civil Engineering</Link>
          </div>
        </div>
      )}
    </header>
  );
}
